import React, { useCallback } from 'react';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

import Flex from '@components/layout/flex';
import Checkbox from '@components/form/checkbox';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

import { SimplePaymentMethod } from '@services/payment/types/simple-payment-method';
import { CardBrands } from '@services/payment/types/card-brands';

interface MyCardCtrlProps {
  className?: string;
  creditCard: SimplePaymentMethod;
  onSelection?: (card?: SimplePaymentMethod) => void;
  onDelete?: (card?: SimplePaymentMethod) => void;
  active: boolean;
  disableCheckbox?: boolean;
}

const cardnNameMapper = (brand: CardBrands) => {
  switch (brand) {
    case 'amex':
      return 'AMEX';
    case 'diners':
      return 'Diners';
    case 'discover':
      return 'Discover';
    case 'jcb':
      return 'JCB';
    case 'mastercard':
      return 'Mastercard';
    case 'unionpay':
      return 'UnionPay';
    case 'visa':
      return 'Visa';
    case 'unknown':
    default:
      return 'Other';
  }
};

export const MyCardCtrl: React.FC<MyCardCtrlProps> = ({
  className,
  creditCard,
  active,
  disableCheckbox,
  onSelection,
  onDelete,
}) => {
  const handleSelection = useCallback(() => {
    return onSelection ? onSelection(active ? undefined : creditCard) : null;
  }, [onSelection, active, creditCard]);

  React.useEffect(() => {
    handleSelection();
  }, [handleSelection]);

  return (
    <MotionOpacity className={className}>
      <Checkbox
        active={active}
        onClick={handleSelection}
        title={<Translate id="PAYMENT.CARD.SAVED.TITLE" />}
        disabled={disableCheckbox}
        text={
          <CreditCardInfo alignItems="center" justifyContent="space-between">
            <CardIconWrapper brand={creditCard.brand}>
              <Typography weight="700" color="white">
                {cardnNameMapper(creditCard.brand)}
              </Typography>
            </CardIconWrapper>
            <Flex alignItems="center">
              <StyledType hasMargin>
                <Translate id="PAYMENT.CARD.SAVED.ENDING" />
              </StyledType>
              <StyledType weight="700">{creditCard.last4}</StyledType>
            </Flex>

            {onDelete ? (
              <Remove
                weight="700"
                type="link"
                onClick={() => onDelete(creditCard)}
              >
                Remove
              </Remove>
            ) : null}
          </CreditCardInfo>
        }
      />
    </MotionOpacity>
  );
};

export default MyCardCtrl;

const CardIconWrapper = styled.div<{ brand: CardBrands }>`
  padding: 4px 8px;
  background: ${({ brand }) =>
    brand === 'visa'
      ? css`
          ${({ theme }) => theme.colors.visa}
        `
      : brand === 'mastercard'
      ? css`
          ${({ theme }) => theme.colors.mastercard}
        `
      : brand === 'amex'
      ? css`
          ${({ theme }) => theme.colors.amex}
        `
      : brand === 'diners'
      ? css`
          ${({ theme }) => theme.colors.diners}
        `
      : brand === 'discover'
      ? css`
          ${({ theme }) => theme.colors.discover}
        `
      : brand === 'jcb'
      ? css`
          ${({ theme }) => theme.colors.jcb}
        `
      : brand === 'unionpay'
      ? css`
          ${({ theme }) => theme.colors.unionpay}
        `
      : css`
          ${({ theme }) => theme.colors.black}
        `};
  // margin-right: 4px;
  border-radius: 16px;
  max-width: 120px;
  text-align: center;

  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledType = styled(Typography)<{ hasMargin?: boolean }>`
  ${({ hasMargin }) =>
    hasMargin
      ? css`
          margin-right: 4px;
        `
      : null}
  color: ${({ theme }) => theme.colors.primary};
`;

const CreditCardInfo = styled(Flex)``;

const Remove = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;
